declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

export const GA_MEASUREMENT_ID = 'G-7EGCMK5XM1';
export const GA_PROPERTY_ID = '552891153';
export const GA_STREAM_ID = '15721846004';
export const GA_STREAM_NAME = 'passphotopro';
export const GA_STREAM_URL = 'https://passphotopro.com';

let isGaConfigured = false;
let configuredGaId = '';

/**
 * Ensures Google Analytics (gtag.js) is properly loaded and initialized.
 * Dynamically injects script tag if missing from DOM and safely executes gtag config once.
 */
export function ensureGoogleTags(ga4Id?: string): void {
  if (typeof window === 'undefined') return;
  const targetGa4 = (ga4Id && ga4Id.trim()) || GA_MEASUREMENT_ID;

  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function () {
      window.dataLayer!.push(arguments);
    };
  }

  // Ensure Google Tag script exists in DOM
  const selector = `script[src*="googletagmanager.com/gtag/js?id=${targetGa4}"]`;
  if (!document.querySelector(selector)) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${targetGa4}`;
    document.head.insertBefore(script, document.head.firstChild);
  }

  if (!isGaConfigured || configuredGaId !== targetGa4) {
    try {
      window.gtag('js', new Date());
      window.gtag('config', targetGa4, {
        send_page_view: true,
        cookie_flags: 'SameSite=None;Secure',
      });
      isGaConfigured = true;
      configuredGaId = targetGa4;
    } catch {}
  }
}

// Auto-run on script execution in client environment
if (typeof window !== 'undefined') {
  ensureGoogleTags();
}

/**
 * Get or create persistent anonymous visitor ID in localStorage
 */
export function getOrCreateVisitorId(): string {
  if (typeof window === 'undefined') return 'server_id';
  try {
    let vid = localStorage.getItem('omni_visitor_id');
    if (!vid) {
      vid = 'vid_' + Math.random().toString(36).substring(2, 10) + '_' + Date.now().toString(36);
      localStorage.setItem('omni_visitor_id', vid);
    }
    return vid;
  } catch {
    return 'temp_vid_' + Date.now();
  }
}

/**
 * Safely invoke GA4 gtag function.
 * Guaranteed to never throw or break application logic if GA script is blocked or unavailable.
 */
export function trackGaEvent(eventName: string, params?: Record<string, any>): void {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', eventName, params);
    }
  } catch (err) {
    // Non-blocking error safety
  }
}

/**
 * Track SPA route changes for public website views.
 * Sends event to GA4 gtag if available AND posts live pageview telemetry to local server.
 */
export function trackPageView(pagePath: string, pageTitle?: string): void {
  try {
    if (!pagePath || pagePath.toLowerCase().includes('/admin')) {
      return;
    }
    const resolvedTitle = pageTitle || (typeof document !== 'undefined' ? document.title : 'OmniFetch Pro');
    const vid = getOrCreateVisitorId();

    // 1. Google Analytics Client Tag
    if (typeof window !== 'undefined') {
      ensureGoogleTags();
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'page_view', {
          page_path: pagePath,
          page_title: resolvedTitle,
          page_location: window.location.href,
        });
      }
    }

    // 2. Real-time Local Server Telemetry
    if (typeof window !== 'undefined') {
      fetch('/api/telemetry/pageview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visitorId: vid,
          pagePath,
          pageTitle: resolvedTitle,
        }),
      }).catch(() => {});
    }
  } catch (err) {
    // Non-blocking catch
  }
}

let heartbeatInitialized = false;

/**
 * Initialize 30-second live visitor heartbeat ping to maintain real active session metrics
 */
export function initAnalyticsHeartbeat(): void {
  if (typeof window === 'undefined' || heartbeatInitialized) return;
  heartbeatInitialized = true;

  const sendHeartbeat = () => {
    try {
      const pagePath = window.location.pathname;
      if (pagePath.toLowerCase().includes('/admin')) return;
      const vid = getOrCreateVisitorId();
      fetch('/api/telemetry/heartbeat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visitorId: vid,
          pagePath,
        }),
      }).catch(() => {});
    } catch {}
  };

  sendHeartbeat();
  setInterval(sendHeartbeat, 30000);
}

/**
 * Safe custom events with non-sensitive aggregate parameters ONLY.
 */
export function trackDownloadAttempt(platform?: string): void {
  trackGaEvent('video_download_attempt', {
    platform: (platform || 'unknown').toLowerCase(),
  });
}

export function trackDownloadSuccess(platform?: string, provider?: string): void {
  trackGaEvent('video_download_success', {
    platform: (platform || 'unknown').toLowerCase(),
    provider: (provider || 'default').toLowerCase(),
  });
}

export function trackDownloadFailure(platform?: string): void {
  trackGaEvent('video_download_failure', {
    platform: (platform || 'unknown').toLowerCase(),
  });
}

/**
 * Diagnostic test trigger to verify Google Analytics data collection in real time
 */
export async function sendDiagnosticGaEvent(
  measurementId: string = GA_MEASUREMENT_ID,
  propertyId: string = GA_PROPERTY_ID,
  streamId: string = GA_STREAM_ID
): Promise<{ success: boolean; status: number; message: string }> {
  // 1. Fire in client gtag
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'admin_diagnostic_ping', {
        property_id: propertyId,
        stream_id: streamId,
        stream_name: GA_STREAM_NAME,
        timestamp: Date.now(),
      });
    }
  } catch {}

  // 2. Fire backend probe to Google Analytics endpoint
  try {
    const res = await fetch('/api/analytics/test-ga', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        measurementId,
        propertyId,
        streamId,
      }),
    });
    const data = await res.json();
    return {
      success: data.success || res.status === 200,
      status: data.status || res.status,
      message: data.message || 'تم إرسال حدث الفحص بنجاح',
    };
  } catch (err: any) {
    return {
      success: false,
      status: 500,
      message: err?.message || 'خطأ في الاتصال',
    };
  }
}


